/**
 * Risk Icon Renderer - Risk/vulnerability node icon
 *
 * Renders a chip/vulnerability icon used for representing risk nodes
 * in the graph visualization with severity-based colors.
 */

import { getIconConfig } from '@/components/icons/nodeIcons/config/iconConfig';
import { getThemeColors } from '@/components/icons/nodeIcons/config/themeColors';
import {
  calculateIconPosition,
  drawCircleBackground,
  setupCanvas,
} from '@/components/icons/nodeIcons/renderers/base';
import { RiskSeverity } from '@/types';

/**
 * Create risk icon data URL
 *
 * Generates a chip/vulnerability icon with circular background for use as
 * a risk node image in the graph visualization. Color is based on severity.
 *
 * @param currentTheme - Current theme ('light' or 'dark')
 * @param severity - Risk severity level
 * @param isHighlighted - Whether the icon should appear highlighted
 * @returns Data URL string of the rendered icon
 */
export const createRiskIconDataURL = (
  currentTheme: 'light' | 'dark',
  severity: RiskSeverity,
  isHighlighted: boolean = false
): string => {
  const config = getIconConfig('risk');
  const colors = getThemeColors('risk', currentTheme, isHighlighted, severity);
  const { canvas, ctx, size, centerX, centerY } = setupCanvas(config);

  // Draw circle background and border
  drawCircleBackground(ctx, centerX, centerY, size, config.scale, colors);

  // Calculate icon position
  const {
    x: riskX,
    y: riskY,
    scale: riskScale,
  } = calculateIconPosition(centerX, centerY, size, config);

  // Start drawing the risk icon path
  ctx.save();
  ctx.translate(riskX, riskY);
  ctx.scale(riskScale, riskScale);

  // Setup canvas context for chip icon
  ctx.strokeStyle = 'rgba(0,0,0,0)';
  ctx.miterLimit = 4;
  ctx.fillStyle = '#D9D9D9';

  // Draw the chip icon path
  ctx.beginPath();
  ctx.moveTo(11.2593, 4.31805);
  ctx.lineTo(9.28256, 4.31805);
  ctx.bezierCurveTo(8.96601, 3.78154, 8.52986, 3.3207, 8.00226, 2.96991);
  ctx.lineTo(9.14891, 1.84875);
  ctx.lineTo(8.15703, 0.878906);
  ctx.lineTo(6.63051, 2.3715);
  ctx.bezierCurveTo(6.30692, 2.29583, 5.97629, 2.25456, 5.6316, 2.25456);
  ctx.bezierCurveTo(5.2869, 2.25456, 4.95628, 2.29583, 4.63972, 2.3715);
  ctx.lineTo(3.10617, 0.878906);
  ctx.lineTo(2.11429, 1.84875);
  ctx.lineTo(3.2539, 2.96991);
  ctx.bezierCurveTo(2.73334, 3.3207, 2.29719, 3.78154, 1.98063, 4.31805);
  ctx.lineTo(0.00390625, 4.31805);
  ctx.lineTo(0.00390625, 5.69371);
  ctx.lineTo(1.47414, 5.69371);
  ctx.bezierCurveTo(1.43897, 5.92069, 1.41083, 6.14768, 1.41083, 6.38154);
  ctx.lineTo(1.41083, 7.06937);
  ctx.lineTo(0.00390625, 7.06937);
  ctx.lineTo(0.00390625, 8.44503);
  ctx.lineTo(1.41083, 8.44503);
  ctx.lineTo(1.41083, 9.13285);
  ctx.bezierCurveTo(1.41083, 9.36672, 1.43897, 9.5937, 1.47414, 9.82068);
  ctx.lineTo(0.00390625, 9.82068);
  ctx.lineTo(0.00390625, 11.1963);
  ctx.lineTo(1.98063, 11.1963);
  ctx.bezierCurveTo(2.71223, 12.4276, 4.06991, 13.2598, 5.6316, 13.2598);
  ctx.bezierCurveTo(7.19328, 13.2598, 8.55096, 12.4276, 9.28256, 11.1963);
  ctx.lineTo(11.2593, 11.1963);
  ctx.lineTo(11.2593, 9.82068);
  ctx.lineTo(9.78906, 9.82068);
  ctx.bezierCurveTo(9.82423, 9.5937, 9.85237, 9.36672, 9.85237, 9.13285);
  ctx.lineTo(9.85237, 8.44503);
  ctx.lineTo(11.2593, 8.44503);
  ctx.lineTo(11.2593, 7.06937);
  ctx.lineTo(9.85237, 7.06937);
  ctx.lineTo(9.85237, 6.38154);
  ctx.bezierCurveTo(9.85237, 6.14768, 9.82423, 5.92069, 9.78906, 5.69371);
  ctx.lineTo(11.2593, 5.69371);
  ctx.lineTo(11.2593, 4.31805);
  ctx.closePath();

  // Inner chip details
  ctx.moveTo(7.03852, 9.82068);
  ctx.lineTo(4.22468, 9.82068);
  ctx.lineTo(4.22468, 8.44503);
  ctx.lineTo(7.03852, 8.44503);
  ctx.lineTo(7.03852, 9.82068);
  ctx.closePath();

  ctx.moveTo(7.03852, 7.06937);
  ctx.lineTo(4.22468, 7.06937);
  ctx.lineTo(4.22468, 5.69371);
  ctx.lineTo(7.03852, 5.69371);
  ctx.lineTo(7.03852, 7.06937);
  ctx.closePath();

  ctx.fill();
  ctx.stroke();
  ctx.restore();

  // Convert to data URL with higher quality
  return canvas.toDataURL('image/png', 1.0);
};
